import I18n from 'lib/i18n'
import isEmailValid from 'lib/utils/is-email-valid'
import GraphemeSplitter from 'grapheme-splitter'

const splitter = new GraphemeSplitter()

export const getErrorMessage = validationResults => {
  const errorValues = Object.values(validationResults)
  const hasOnlyNull = errorValues.every(value => value === null)

  if (hasOnlyNull) {
    return null
  }

  return I18n.t('courses.form.error_field_empty')
}

export const stringLength = text => (text || '').replace(/([\W])(?=\1)/gi, '').trim().length

export const required = value => (stringLength(value) === 0 ? I18n.t('courses.form.error_field_empty') : null)

export const arrayLength = arr => {
  let start = 0
  let end = arr.length - 1
  while (start < end && arr[start] === '') {
    start++
  }
  while (end > start && arr[end] === '') {
    end--
  }
  return arr.slice(start, end + 1).length
}

export const requireBookingTime = value => {
  if (value.length) {
    if (value.filter(bt => bt.isEditing).length) {
      return ' '
    } else {
      return null
    }
  } else {
    return I18n.t('courses.form.error_field_empty')
  }
}

export const requiredLocation = (locationId, locations) => {
  if (locations.filter(l => l.id === locationId).length) {
    return isNaN(locationId) || !locationId ? I18n.t('courses.form.error_field_empty') : null
  }
  return I18n.t('courses.form.error_field_empty')
}

export const requiredStudentAddress = (locations, locationId, studentAddress) => {
  if (locationId) {
    const selectedLocation = locations.filter(l => l.id === locationId)
    const selectedLocationType = selectedLocation.length ? selectedLocation[0].locationType : null
    return selectedLocationType === 'in_house' ? required(studentAddress) : null
  }
  return null
}

export const requiredPhone = (phone, phoneValid) => {
  const phoneError = required(phone)
  return !phoneError && !phoneValid ? I18n.t('errors.invalid_phone_number') : phoneError
}

export const requiredPaymentMethod = (paymentMethod, useCredit, selectedTotalPrice, creditAvailable) => {
  if (useCredit) {
    const isEnoughToPay = creditAvailable - selectedTotalPrice >= 0
    return isEnoughToPay ? null : required(paymentMethod)
  } else {
    return required(paymentMethod)
  }
}

export const requiredFullName = value =>
  required(value) || value.trim().split(' ').length < 2
    ? I18n.t('devise.sessions.new_student.error_no_full_name')
    : null

export const requiredArray = value => (value.length === 0 ? I18n.t('courses.form.error_field_empty') : null)

export const requiredLength = (value, length) => {
  const graphemeClustersString = splitter.splitGraphemes(value || '')
  const stringLength = arrayLength(graphemeClustersString)
  return stringLength < length ? I18n.t('errors.not_long_enough', { count: length - stringLength }) : null
}

export const requiredEmail = value =>
  stringLength(value) === 0
    ? I18n.t('courses.form.error_field_empty')
    : !isEmailValid(value.trim())
    ? I18n.t('courses.show_share.email_error_not_email')
    : null

export const requiredCategoryId = value => {
  return value ? null : I18n.t('courses.form.error_field_empty')
}

export const requiredQuestionAnswers = (values, categoryQuestions, checkType) => {
  const { questionAnswers, customAnswers, categoryId } = values || {}

  // start the preliminary check for categoryId and required questions for the selected category
  if (!categoryId) return I18n.t('courses.form.error_field_empty')

  let requiredQuestionsForSelectedCategory = []

  if (categoryId && categoryId !== '0' && categoryQuestions[categoryId.value ? categoryId.value : categoryId]) {
    categoryQuestions[categoryId.value ? categoryId.value : categoryId].forEach(categoryQuestion => {
      if (categoryQuestion.required) {
        requiredQuestionsForSelectedCategory.push(categoryQuestion)
      }
    })
  }

  if (requiredQuestionsForSelectedCategory.length === 0) return null
  // end the start the preliminary check for categoryId and required questions for the selected category

  const requiredAnswersForSelectedCategory = requiredQuestionsForSelectedCategory.reduce(
    (answersByQuestionId, requiredQuestionForSelectedCategory) => {
      answersByQuestionId[requiredQuestionForSelectedCategory.id] =
        questionAnswers[requiredQuestionForSelectedCategory.id]
      return answersByQuestionId
    },
    {}
  )

  // start custom Logic
  const customAnswersIds = Object.entries(customAnswers)
    .map(([customAnswerKey, customAnswerValue]) => (customAnswerValue !== '' ? parseInt(customAnswerKey) : null))
    .filter(customAnswerKey => customAnswerKey)

  const requiredCustomAnswers = {}

  requiredQuestionsForSelectedCategory.forEach(requiredQuestionForSelectedCategory => {
    if (
      questionAnswers &&
      questionAnswers[requiredQuestionForSelectedCategory.id] &&
      questionAnswers[requiredQuestionForSelectedCategory.id].length > 0
    ) {
      questionAnswers[requiredQuestionForSelectedCategory.id].forEach(
        questionAnswerForRequiredQuestionForSelectedCategory => {
          if (!customAnswersIds.includes(questionAnswerForRequiredQuestionForSelectedCategory.id)) {
            requiredQuestionForSelectedCategory.answers.forEach(requiredQuestionForSelectedCategoryAnswer => {
              if (
                requiredQuestionForSelectedCategoryAnswer.id ===
                  parseInt(questionAnswerForRequiredQuestionForSelectedCategory.id) &&
                requiredQuestionForSelectedCategoryAnswer.answer_type === 'custom' &&
                !customAnswers[questionAnswerForRequiredQuestionForSelectedCategory.id]
              ) {
                requiredCustomAnswers[questionAnswerForRequiredQuestionForSelectedCategory.id] = I18n.t(
                  'courses.form.error_field_empty'
                )
              }
            })
          }
        }
      )
    }
  })
  // end custom Logic

  // start studentInformation check logic
  const areAllRequiredQuestionsAnswered = requiredQuestionsForSelectedCategory.every(
    requiredQuestionForSelectedCategory => {
      let isRequiredQuestionForSelectedCategoryAnswered

      const requiredAnswerForSelectedCategory =
        requiredAnswersForSelectedCategory[requiredQuestionForSelectedCategory.id]

      if (requiredAnswerForSelectedCategory.length === 0) return false

      const requiredAnswerIds = requiredAnswerForSelectedCategory.map(answer => parseInt(answer.id))
      const requiredAnswersWithTypes = requiredQuestionForSelectedCategory.answers.filter(answerForRequiredQuestion =>
        requiredAnswerIds.includes(answerForRequiredQuestion.id)
      )
      const requiredAnswersWithCustomTypes = requiredAnswersWithTypes.filter(
        requiredAnswerWithType => requiredAnswerWithType.answer_type === 'custom'
      )

      if (requiredAnswersWithCustomTypes.length === 0) {
        isRequiredQuestionForSelectedCategoryAnswered = true
      } else {
        isRequiredQuestionForSelectedCategoryAnswered = requiredAnswersWithCustomTypes.every(
          requiredAnswerWithCustomType => {
            return (
              Object.keys(customAnswers).length !== 0 &&
              customAnswers[requiredAnswerWithCustomType.id] &&
              customAnswers[requiredAnswerWithCustomType.id].trim() !== ''
            )
          }
        )
      }

      return isRequiredQuestionForSelectedCategoryAnswered
    }
  )
  // end studentInformation check logic

  if (checkType === 'standard') {
    return Object.fromEntries(
      Object.entries(requiredAnswersForSelectedCategory).map(
        ([requiredAnswerForSelectedCategoryKey, requiredAnswerForSelectedCategoryValue]) => {
          return [
            requiredAnswerForSelectedCategoryKey,
            requiredAnswerForSelectedCategoryValue.length !== 0 ? null : I18n.t('courses.form.error_field_empty')
          ]
        }
      )
    )
  }

  if (checkType === 'custom') {
    return requiredCustomAnswers || null
  }

  if (checkType === 'studentInformation') {
    return areAllRequiredQuestionsAnswered ? null : I18n.t('courses.form.error_field_empty')
  }
}
